import { ApolloError, useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { ReactElement, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import Modal from 'components/v2/Modal'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { SelectDow } from 'features/RoutingProjects/components/SelectDow'
import { ILocationRemoveForm } from 'features/RoutingProjects/types'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'
import { getAvailableDowsByRoutesAndLocations } from 'features/RoutingProjects/utils'
import {
  CHANGE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS,
  REMOVE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS
} from 'features/RoutingProjects/mutations'
import { RoutingProjectDow } from 'gql/generated/graphql'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'
import { removeLocationFromPlanByDows } from 'features/RoutingProjects/models'
import { isEmpty, isEqual } from 'lodash'
import { WarningNotification } from 'components/WarningNotification'

interface ILocationRemoveModalProps {
  onClose: () => void
  locationId: string
}

export const LocationRemoveModal = ({ onClose, locationId }: ILocationRemoveModalProps): ReactElement | null => {
  const intl = useIntl()
  const {
    selectedDow,
    planModel,
    setPlanModel,
    currentOptimization,
    routingProjectModelId,
    saveUpdatedOptimizations,
    adjustMapBoundsByAffectedRoutes,
    updateOptimizations
  } = usePlanContext()
  const [error, setError] = useState<ApolloError | null>(null)

  const [removeRoutingProjectDatasetLocations] = useMutation(REMOVE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS)
  const [changePlanDatasetLocations] = useMutation(CHANGE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS)
  const location = planModel.dataset?.locations?.find((loc) => loc.id === locationId)
  const updatedRoute = currentOptimization?.routes.find((r) => r.stops.some((stop) => stop.location.id === locationId))
  if (!location || !updatedRoute || !selectedDow) {
    return null
  }
  const availableDows = getAvailableDowsByRoutesAndLocations({
    routes: [updatedRoute],
    optimizations: planModel.optimizations,
    locations: [location],
    shouldFilterLockedRoutes: true
  })
  const initialValues: ILocationRemoveForm = {
    dows: availableDows
  }

  const handleSubmit = async (
    values: ILocationRemoveForm,
    { setSubmitting }: FormikHelpers<ILocationRemoveForm>
  ): Promise<void> => {
    try {
      const initialPlanModel = { ...planModel }
      let updatedPlanModel = { ...planModel }

      const planWithoutLocation = removeLocationFromPlanByDows({
        plan: planModel,
        locationId,
        dows: values.dows
      })
      updatedPlanModel = {
        ...planModel,
        ...planWithoutLocation
      }
      const optimizationsAffected = initialPlanModel.optimizations.filter((optimization) =>
        values.dows.includes(optimization.dow)
      )
      const { planModel: planModelWithUpdatedOptimizations, affectedRoutesMap } = await updateOptimizations({
        planModel: updatedPlanModel,
        optimizations: optimizationsAffected,
        locationIds: [locationId]
      })
      updatedPlanModel = {
        ...planModelWithUpdatedOptimizations
      }

      const currentOptimizationUpdated = updatedPlanModel.optimizations.find(
        (optimization) => optimization.id === currentOptimization?.id
      )

      await saveUpdatedOptimizations({
        planModel: updatedPlanModel,
        optimizations: updatedPlanModel.optimizations,
        affectedRoutesMap
      })

      adjustMapBoundsByAffectedRoutes({
        optimization: currentOptimizationUpdated,
        affectedRoutesIndex: affectedRoutesMap[currentOptimization?.id as string] || []
      })

      setPlanModel({ ...updatedPlanModel })

      const updatedLocation = updatedPlanModel.dataset?.locations?.find((loc) => loc.id === locationId)

      if (isEmpty(updatedLocation)) {
        removeRoutingProjectDatasetLocations({
          variables: {
            input: {
              id: routingProjectModelId,
              plan: {
                id: planModel.id,
                dataset: {
                  locationIDs: [locationId]
                }
              }
            }
          }
        })
      } else {
        changePlanDatasetLocations({
          variables: {
            input: {
              id: routingProjectModelId,
              plan: {
                id: planModel.id,
                dataset: {
                  locations: [
                    {
                      id: locationId,
                      label: location.label,
                      coordinates: {
                        latitude: location.coordinates.latitude.toString(),
                        longitude: location.coordinates.longitude.toString()
                      },
                      dow: updatedLocation.dows
                    }
                  ]
                }
              }
            }
          }
        })
      }
      onClose()
    } catch (error) {
      setError(error as ApolloError)
    }
    setSubmitting(false)
  }

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          {planModel.dataset?.hasDow ? (
            <FormattedMessage
              id="routingProject.updateOptimization.location.remove.title"
              defaultMessage="Remove {locationName} from plan on"
              values={{
                locationName: <strong>{location.label}</strong>
              }}
            />
          ) : (
            <FormattedMessage
              id="routingProject.updateOptimization.location.remove.title.noDow"
              defaultMessage="Delete {locationName} from plan"
              values={{
                locationName: <strong>{location.label}</strong>
              }}
            />
          )}
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { setFieldValue, handleBlur, touched, errors, values, isSubmitting } = props

          const handleDowsChange = (dows: RoutingProjectDow[]): void => {
            setFieldValue('dows', dows)
          }

          const shouldDisplayRemovalWarning = !planModel.dataset?.hasDow || isEqual(values.dows, location.dows)

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                {planModel.dataset?.hasDow && (
                  <Row>
                    <Column>
                      <SelectDow
                        id="dows"
                        values={values.dows}
                        onChange={handleDowsChange}
                        onBlur={handleBlur}
                        touched={touched.dows}
                        errors={errors.dows}
                        label={<FormattedMessage id="routingProjects.locationRemove.dows.label" defaultMessage="DOW" />}
                        placeholder={intl.formatMessage({
                          id: 'routingProjects.locationRemove.dows.placeholder',
                          defaultMessage: 'Select DOW'
                        })}
                        availableDows={availableDows}
                        readOnlyValues={[selectedDow]}
                      />
                    </Column>
                  </Row>
                )}
                {shouldDisplayRemovalWarning && (
                  <WarningNotification>
                    <FormattedMessage
                      id="routingProjects.locationRemove.warning"
                      defaultMessage="This location will be removed completely from the plan dataset."
                    />
                  </WarningNotification>
                )}
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="routingProjects.locationRemove.error"
                        defaultMessage="There was an error applying the changes"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={onClose}>
                    <FormattedMessage id="modal.actions.close" defaultMessage="Close" />
                  </Button>
                  <Button kind="primary" loading={isSubmitting}>
                    <FormattedMessage id="routingProjects.create.submit" defaultMessage="Save" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
